<template>
  <article
    v-if="match && getProfile && (getProfile.isAdmin || getProfile.isTeacher)"
    class="panel is-danger gameAdmin"
  >
    <p class="panel-heading">{{ getProfile.role }} Panel</p>

    <!-- Match info -->
    <div
      class="is-flex is-justify-content-space-between mt-5"
      v-if="moveIndex >= 0 && moves[moveIndex]"
    >
      <b-field
        label="Match Id"
        label-position="on-border"
        type="is-info"
        class="is-flex-grow-1 mx-2"
      >
        <b-input
          readonly
          :value="match.match_id"
          icon-right="copy"
          icon-right-clickable
          @icon-right-click="copyDoiToClipboard(match.match_id)"
        />
      </b-field>

      <b-field
        label="FEN"
        label-position="on-border"
        type="is-info"
        class="is-flex-grow-1 mx-2"
      >
        <template v-slot:label>
          <b-tooltip position="is-right" append-to-body>
            FEN
            <template v-slot:content>
              Click on Copy icon on the right to copy the FEN
            </template>
          </b-tooltip>
        </template>
        <b-input
          readonly
          :value="moves[moveIndex].fen"
          icon-right="copy"
          icon-right-clickable
          @icon-right-click="copyDoiToClipboard(moves[moveIndex].fen)"
        />
      </b-field>

      <b-field
        label="PGN"
        label-position="on-border"
        type="is-info"
        class="is-flex-grow-1 mx-2"
      >
        <template v-slot:label>
          <b-tooltip position="is-right" append-to-body>
            PGN
            <template v-slot:content>
              Click on Copy icon on the right to copy the PGN
            </template>
          </b-tooltip>
        </template>
        <b-input
          readonly
          :value="getFullPgn(moves[moveIndex].pgn)"
          icon-right="copy"
          icon-right-clickable
          @icon-right-click="
            copyDoiToClipboard(getFullPgn(moves[moveIndex].pgn))
          "
        />
      </b-field>
    </div>

    <!-- Match properties -->
    <div
      class="gameAdmin_modifyMatch mt-5 mx-2"
      v-if="getProfile.isAdmin && match.match_id"
    >
      <section class="is-flex is-justify-content-center my-4">
        <b-field label="Status" :type="style.status" :message="message.status">
          <b-select v-model="status" placeholder="Status" @input="changeStatus">
            <option value="created" disabled>created</option>
            <option value="canceled" :disabled="match.status !== 'created'">
              canceled
            </option>
            <option value="live">live</option>
            <option value="finished">finished</option>
            <option value="aborted">aborted</option>
          </b-select>
        </b-field>

        <div class="is-flex is-flex-direction-column">
          <b-field
            label="Result"
            :type="style.result"
            :message="message.result"
          >
            <b-select
              v-model="result"
              placeholder="Result"
              @input="changeResult"
            >
              <option value="">Unfinished</option>
              <option value="abort">abort</option>
              <option value="white_abort">white_abort</option>
              <option value="black_abort">black_abort</option>
              <option value="forfeit">forfeit</option>
              <option value="checkmate">checkmate</option>
              <option value="stalemate">stalemate</option>
              <option value="threefold_repetition">threefold_repetition</option>
              <option value="insufficient_material">
                insufficient_material
              </option>
              <option value="draw">draw</option>
              <option value="game over" disabled>game over</option>
              <option value="timeout">timeout</option>
              <option value="bare king">bare king</option>
              <option value="resign">resign</option>
              <option value="draw by agreement">draw by agreement</option>
              <option value="disqualified">disqualified</option>
              <option value="invalid">invalid</option>
            </b-select>
          </b-field>

          <b-button
            type="is-warning"
            @click="
              result = 'timeout';
              changeResult();
            "
          >
            timeout
          </b-button>
          <b-button
            type="is-dark"
            @click="
              result = 'forfeit';
              changeResult();
            "
          >
            forfeit
          </b-button>
          <b-button
            type="is-black"
            @click="
              result = 'abort';
              changeResult();
            "
          >
            abort
          </b-button>
          <b-button
            type="is-danger"
            @click="
              result = 'disqualified';
              changeResult();
            "
          >
            disqualified
          </b-button>
        </div>

        <div class="is-flex is-flex-direction-column">
          <b-field
            label="White"
            :type="style.whiteScore"
            :message="message.whiteScore"
          >
            <b-select v-model="whiteScore" placeholder="Score">
              <option :value="0">0</option>
              <option :value="0.5">½</option>
              <option :value="1">1</option>
              <option :value="null">null</option>
            </b-select>
          </b-field>

          <b-button
            type="is-success"
            :disabled="!result"
            @click="
              whiteScore = 1;
              blackScore = 0;
            "
          >
            win
          </b-button>
          <b-button
            type="is-warning"
            :disabled="!result"
            @click="
              whiteScore = 0.5;
              blackScore = 0.5;
            "
          >
            draw
          </b-button>
          <b-button
            type="is-danger"
            :disabled="!result"
            @click="
              whiteScore = 0;
              blackScore = 1;
            "
          >
            loss
          </b-button>
          <b-button
            type="is-black"
            :disabled="!result"
            @click="
              whiteScore = 0;
              blackScore = 0;
            "
          >
            0 - 0
          </b-button>
        </div>

        <b-field
          label="Black"
          :type="style.blackScore"
          :message="message.blackScore"
        >
          <b-select v-model="blackScore" placeholder="Score">
            <option :value="0">0</option>
            <option :value="0.5">½</option>
            <option :value="1">1</option>
            <option :value="null">null</option>
          </b-select>
        </b-field>

        <div class="is-flex is-flex-direction-column">
          <b-field
            label="Minutes"
            class="match__input"
            :type="style.minutes"
            :message="message.minutes"
          >
            <b-numberinput
              v-model="minutes"
              :min="1"
              :controls="false"
            ></b-numberinput>
          </b-field>

          <template>
            <b-field class="match__input" type="is-danger">
              <b-numberinput
                v-model="extendMinutes"
                :min="1"
                :controls="false"
              ></b-numberinput>
            </b-field>
            <b-button
              type="is-danger"
              :disabled="!extendMinutes"
              @click="extendMatch"
            >
              Extend
            </b-button>
          </template>
        </div>

        <div class="is-flex is-flex-direction-column">
          <b-field
            label="Start"
            :type="style.startDate"
            :message="message.startDate"
          >
            <b-datetimepicker
              v-model="startDate"
              placeholder="Start Time"
              editable
              icon="calendar"
              :disabled="disabled.startDate"
            >
              <template #left>
                <b-button
                  label="Now"
                  type="is-primary"
                  icon-left="clock"
                  @click="startedAt = new Date()"
                />
              </template>

              <template #right>
                <b-button
                  label="Clear"
                  type="is-danger"
                  icon-left="close"
                  outlined
                  @click="startedAt = null"
                />
              </template>
            </b-datetimepicker>
          </b-field>

          <b-field
            label-position="on-border"
            label="White time hh:mm:ss"
            :type="style.whiteTime"
            :message="message.whiteTime"
          >
            <b-timepicker
              v-model="whiteTime"
              placeholder="custom white time"
              enable-seconds
              hour-format="24"
              editable
            >
              <b-button
                label="Clear"
                type="is-danger"
                icon-left="close"
                outlined
                @click="whiteTime = null"
              />
            </b-timepicker>
          </b-field>

          <b-field
            label-position="on-border"
            label="Black time hh:mm:ss"
            :type="style.blackTime"
            :message="message.blackTime"
          >
            <b-timepicker
              v-model="blackTime"
              placeholder="custom black time"
              enable-seconds
              hour-format="24"
              editable
            >
              <b-button
                label="Clear"
                type="is-danger"
                icon-left="close"
                outlined
                @click="blackTime = null"
              />
            </b-timepicker>
          </b-field>
        </div>

        <div class="is-flex is-flex-direction-column">
          <b-field label="End" :type="style.endDate" :message="message.endDate">
            <b-timepicker v-model="endDate" icon="clock" editable>
              <b-button
                label="Now"
                type="is-primary"
                icon-left="clock"
                @click="endDate = new Date()"
              />
              <b-button
                label="Clear"
                type="is-danger"
                icon-left="close"
                outlined
                @click="endDate = null"
              />
            </b-timepicker>
          </b-field>

          <b-field>
            <b-checkbox v-model="reverseRating" type="is-warning">
              reverse rating
            </b-checkbox>
          </b-field>

          <b-field>
            <b-checkbox v-model="updateRating" type="is-danger">
              update rating
            </b-checkbox>
          </b-field>
        </div>

        <div class="is-flex is-flex-direction-column">
          <b-field label="Action" type="is-danger" message="Be careful!">
            <b-button type="is-danger" @click="promptConfirm">Update</b-button>
          </b-field>
          <b-button type="is-info" @click="resetValues">Undo</b-button>
        </div>
      </section>

      <h3 class="subtitle is-6 has-text-danger">{{ error }}</h3>
    </div>

    <!-- Player browsers -->
    <div>
      <div v-if="match.player1 && match.player1.browser">
        White browser: {{ match.player1.browser | asBrowser }}
      </div>
      <div v-if="match.player1 && match.player2.browser">
        Black browser: {{ match.player2.browser | asBrowser }}
      </div>
    </div>

    <div v-if="match.result && moves.length">
      <MoveTimelineChart
        :match="match"
        :moves="moves"
        :blackToStart="blackToStart"
        :showFocus="getProfile.isAdmin"
      />
      <MoveChart :match="match" :moves="moves" :blackToStart="blackToStart" />
    </div>
  </article>
</template>

<script>
import MoveChart from "../charts/MoveChart.vue";
import MoveTimelineChart from "../charts/MoveTimelineChart.vue";
import { mapGetters } from "vuex";
import { firestore } from "@/services/firebase";
import { MatchesAPI } from "@/services/api";
import { formatDate } from "vue-chess-shared/utils/date-helper";
import firebase from "firebase/app";

export default {
  components: { MoveChart, MoveTimelineChart },
  name: "GameAdmin",

  props: ["match", "moves", "moveIndex", "blackToStart"],

  data() {
    return {
      status: null,
      result: null,
      whiteScore: null,
      blackScore: null,
      minutes: null,
      whiteTime: null,
      blackTime: null,
      startDate: null,
      endDate: null,
      extendMinutes: null,
      reverseRating: false,
      updateRating: false,
    };
  },

  computed: {
    ...mapGetters(["getUserId", "getProfile"]),

    original() {
      if (!this.match) return {};
      return {
        status: this.status === this.match.status,
        result: this.result === this.match.result,
        whiteScore: this.whiteScore == this.match.player_1_score,
        blackScore: this.blackScore == this.match.player_2_score,
        minutes: this.minutes * 60 === this.match.duration,
        startDate:
          (!this.startDate && !this.match.startedAt) ||
          (this.startDate &&
            this.match.startedAt &&
            this.startDate.getTime() ===
              this.match.startedAt.toDate().getTime()),
        endDate:
          (!this.endDate && !this.match.endedAt) ||
          (this.endDate &&
            this.match.endedAt &&
            this.endDate.getTime() === this.match.endedAt.toDate().getTime()),
      };
    },

    style() {
      return Object.fromEntries(
        Object.entries(this.original).map(([k, v]) => [
          k,
          this.disabled[k] ? "is-success" : v ? "is-info" : "is-danger",
        ])
      );
    },

    disabled() {
      if (!this.match) return {};
      return {
        startDate: this.moves && !!this.moves.length,
      };
    },

    message() {
      if (!this.match) return {};
      const message = {
        status: this.original.status ? "" : `was '${this.match.status}'`,
        result: this.original.result ? "" : `was '${this.match.result}'`,
        whiteScore: this.original.whiteScore
          ? ""
          : `was ${this.match.player_1_score}`,
        blackScore: this.original.blackScore
          ? ""
          : `was ${this.match.player_2_score}`,
        minutes: this.original.minutes ? "" : `was ${this.match.duration / 60}`,
        startDate: this.original.startDate
          ? ""
          : `was ${this.match.startedAt.toDate().toLocaleTimeString()}`,
        endDate: this.original.endDate
          ? ""
          : `was ${
              this.match.endedAt
                ? this.match.endedAt.toDate().toLocaleTimeString()
                : this.match.endedAt
            }`,
      };

      if (this.disabled.startDate) {
        message.startDate = "Match has started";
      }

      return message;
    },

    error() {
      if (this.endDate && this.startDate && this.endDate < this.startDate) {
        return "End Time must be after Start Time";
      }
      // if (this.minutes * 60 < this.match.duration)
      //   return "You can't reduce match duration";

      if (this.status === "finished") {
        if (!this.result || this.result === "abort") {
          return "Invalid Result for a finished match";
        }
        if (!this.result.includes("abort") && this.whiteScore == null) {
          return "Match has finished. Please set White Score";
        }
        if (!this.result.includes("abort") && this.blackScore == null) {
          return "Match has finished. Please set Black Score";
        }
        if (
          this.result !== "forfeit" &&
          !this.result.includes("abort") &&
          this.result !== "disqualified" &&
          this.blackScore + this.whiteScore !== 1
        ) {
          return "Invalid combination of scores for a finished match";
        }
        if (this.startDate == null) {
          return "Finished match must have Start Time";
        }
        if (this.endDate == null) return "Finished match must have End Time";
      } else if (this.status === "aborted") {
        if (this.result !== "abort") {
          return "Match was aborted. Result must be 'abort'";
        }
        if (this.blackScore != null || this.whiteScore != null) {
          return "Match was aborted. Score must be null";
        }
        if (this.endDate == null) return "Aborted match must have End Time";
      } else if (this.status === "live") {
        if (this.result) return "Match is live. Result must be 'Unfinished'";
        if (this.blackScore != null || this.whiteScore != null) {
          return "Match is live. Score must be null";
        }
        if (this.startDate == null) return "Live match must have Start Time";
        if (this.endDate) return "Match is live. End Time must be null";
      } else if (this.status === "created") {
        return "Can't edit created match";
      }
      return "";
    },

    canSubmit() {
      return (
        !this.error &&
        (!Object.values(this.original).every((v) => v) || this.reverseRating)
      );
    },
  },

  filters: {
    asBrowser(info) {
      return `${info.browser} ${info.browserVersion} - ${info.os} ${
        info.osVersion
      } - ${info.screen}${info.mobile ? " mobile" : ""}${
        info.cookies ? "" : " - no cookies"
      }`;
    },
  },

  watch: {
    match: {
      immediate: true,
      handler() {
        this.resetValues();
      },
    },
  },

  methods: {
    copyDoiToClipboard(doi) {
      navigator.clipboard.writeText(doi).then(() => {
        this.$buefy.toast.open("Copied to Clipboard");
      });
    },

    resetValues() {
      if (!this.match) return;
      this.status = this.match.status;
      this.result = this.match.result;
      this.whiteScore =
        this.match.player_1_score != null ? this.match.player_1_score : null;
      this.blackScore =
        this.match.player_2_score != null ? this.match.player_2_score : null;
      this.minutes = this.match.duration / 60;
      this.whiteTime = this.secondsToTime(this.match.whiteDuration);
      this.blackTime = this.secondsToTime(this.match.blackDuration);
      this.startDate = this.match.startedAt
        ? this.match.startedAt.toDate()
        : null;
      this.endDate = this.match.endedAt ? this.match.endedAt.toDate() : null;
    },

    secondsToTime(s) {
      if (!s) return null;
      var hours = Math.floor(s / 3600);
      var minutes = Math.floor((s - hours * 3600) / 60);
      var seconds = s - hours * 3600 - minutes * 60;
      return new Date(2000, 1, 1, hours, minutes, seconds);
    },

    changeStatus() {
      console.log("changeStatus", this.status);
      if (
        this.status === "created" ||
        this.status === "canceled" ||
        this.status === "live"
      ) {
        this.result = "";
        this.whiteScore = null;
        this.blackScore = null;
        this.endDate = null;
      } else if (this.status === "finished") {
        if (!this.endDate) this.endDate = new Date();
      } else if (this.status === "aborted") {
        this.result = "abort";
        this.whiteScore = null;
        this.blackScore = null;
        if (!this.endDate) this.endDate = new Date();
        else if (this.endDate < this.startDate) this.endDate = this.startDate;
      }
    },

    changeResult() {
      console.log("changeResult", this.result);
      if (!this.result) {
        this.status = "live";
      } else if (this.result === "abort") {
        this.status = "aborted";
      } else {
        this.status = "finished";
        if (this.result === "forfeit" || this.result === "invalid") {
          this.whiteScore = 0;
          this.blackScore = 0;
        } else if (this.result === "timeout") {
          this.whiteScore = this.moves ? this.moves.length % 2 : 0;
          this.blackScore = 1 - this.whiteScore;
          this.updateRating = true;
        }
      }
      this.changeStatus();
    },

    extendMatch() {
      if (!this.extendMinutes) return;

      const minutesSinceEnd = this.endDate
        ? Math.ceil((new Date() - this.endDate) / 60000)
        : 0;
      this.status = "live";
      this.changeStatus();

      console.log(minutesSinceEnd, this.minutes, this.extendMinutes);
      const extraMinutes = minutesSinceEnd + this.extendMinutes;

      if (!this.whiteTime) {
        this.whiteTime = this.secondsToTime(this.match.duration);
      }
      this.whiteTime = new Date(
        this.whiteTime.getTime() + extraMinutes * 60000
      );
      if (!this.blackTime) {
        this.blackTime = this.secondsToTime(this.match.duration);
      }
      this.blackTime = new Date(
        this.blackTime.getTime() + extraMinutes * 60000
      );
    },

    promptConfirm() {
      this.$buefy.dialog.confirm({
        title: "Match Modification!",
        message: `Be extremely careful with your changes.\n
                  Are you sure about modifying this match?`,
        cancelText: "Cancel",
        confirmText: "Modify",
        type: "is-danger",
        trapFocus: true,
        onConfirm: () => this.adminModifyMatch(),
      });
    },

    async adminModifyMatch() {
      console.log("adminModifyMatch", this.whiteScore, this.blackScore);

      // Update firestore
      const matchRef = firestore.collection("matches").doc(this.match.match_id);
      matchRef.update({
        status: this.status,
        result: this.result,
        player_1_score: this.whiteScore,
        player_2_score: this.blackScore,
        duration: this.minutes * 60,
        whiteDuration: this.whiteTime
          ? (this.whiteTime.getTime() -
              new Date(this.whiteTime.toDateString()).getTime()) /
            1000
          : null,
        blackDuration: this.blackTime
          ? (this.blackTime.getTime() -
              new Date(this.blackTime.toDateString()).getTime()) /
            1000
          : null,
        startedAt: firebase.firestore.Timestamp.fromDate(this.startDate),
        endedAt: this.endDate
          ? firebase.firestore.Timestamp.fromDate(this.endDate)
          : null,
        endedBy: this.getUserId,
      });

      await MatchesAPI.adminPutMatch(this.getUserId, this.match.match_id, {
        // Save to SQL DB
        result: this.result,
        whiteScore: this.whiteScore,
        blackScore: this.blackScore,
        duration: this.minutes * 60,
        startDate: this.startDate,
        endDate: this.endDate,
        reverseRating: this.reverseRating,
        updateRating: this.updateRating,
      });
      this.reverseRating = false;
      this.updateRating = false;
    },

    getFullPgn(pgn) {
      return `[Event "${
        this.match.tournamentId
          ? "Tournament " + this.match.tournamentId
          : "Normal Game"
      }"]
[Site "${
        this.match.tournamentId ? "ChessSquad Tournament" : "ChessSquad Lobby"
      }"]
[Date "${formatDate(this.match.createdAt, "YYYY.MM.DD")}"]
[Round "${this.match.roundNumber ? this.match.roundNumber : "?"}"]
[White "${this.getProfile.isAdmin ? this.match.player1.last_name : "_"}, ${
        this.match.player1.first_name
      }"]
[Black "${this.getProfile.isAdmin ? this.match.player2.last_name : "_"}, ${
        this.match.player2.first_name
      }"]
[Result "${
        this.match.player_1_score == null
          ? "?"
          : this.match.player_1_score === 0.5
          ? "1/2"
          : this.match.player_1_score
      }-${
        this.match.player_2_score == null
          ? "?"
          : this.match.player_2_score === 0.5
          ? "1/2"
          : this.match.player_2_score
      }"]
[PlyCount "${this.moves.length}"]
${pgn}`;
    },
  },
};
</script>

<style scoped>
.gameAdmin {
  margin-bottom: 100px !important;
}

.match__input {
  width: 100px;
}
</style>
